import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  initialTags?: string[];
  isPending: boolean;
  onSubmit: (input: { changelog: string; activate: boolean; tags: string[] }) => void;
}

export function SaveVersionModal({ open, onClose, initialTags = [], isPending, onSubmit }: Props) {
  const [changelog, setChangelog] = useState('');
  const [activate, setActivate] = useState(true);
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: open 토글 시점에만 시드. initialTags 가 동일 모달 내에서 바뀌어도 폼 입력을 덮어쓰지 않도록 의도적으로 제외.
  useEffect(() => {
    if (open) {
      setChangelog('');
      setActivate(true);
      setTags(initialTags);
      setTagDraft('');
    }
  }, [open]);

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || tags.includes(t)) {
      setTagDraft('');
      return;
    }
    setTags([...tags, t]);
    setTagDraft('');
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isPending) return;
    onSubmit({ changelog: changelog.trim(), activate, tags });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="새 버전으로 저장"
      description="변경 사유와 태그를 적어두면 운영에 도움이 됩니다."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button type="submit" form="rp-save-form" disabled={isPending}>
            {isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      }
    >
      <form id="rp-save-form" onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-save-changelog">
            변경 메모 (선택)
          </label>
          <Textarea
            id="rp-save-changelog"
            rows={2}
            value={changelog}
            onChange={(e) => setChangelog(e.target.value)}
            placeholder="예: 답변 톤을 더 단정하게 — 어조 규칙 보강"
          />
        </div>

        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-save-tag">
            태그
          </label>
          <div className="flex gap-2">
            <Input
              id="rp-save-tag"
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              placeholder="예: production"
              onKeyDown={(e) => {
                // 한글 IME composition 중 Enter 는 무시 — 마지막 글자가 별도 태그로 빠지는 회귀 방지.
                if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={addTag}
              disabled={!tagDraft.trim()}
              className="shrink-0 whitespace-nowrap"
            >
              추가
            </Button>
          </div>
          {tags.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-surface-muted px-2 py-0.5 text-xs"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => setTags(tags.filter((x) => x !== t))}
                    className="text-foreground-muted hover:text-destructive"
                    aria-label="태그 제거"
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={activate}
            onChange={(e) => setActivate(e.target.checked)}
            className="size-4"
          />
          저장과 동시에 활성 버전으로 사용
        </label>
      </form>
    </Modal>
  );
}
